import { useEffect, useMemo, useState } from 'react'
import { Button, Input, Select } from '@/shared/ui'
import { toast } from '@/shared/model/toast'
import {
  integrationsApi,
  useAction,
  errorText,
  type BitrixMeta,
  type BitrixPreviewResult,
  type Catalog,
  type Secrets,
  type SecretHint,
  type Variable,
} from '@/entities/integration'
import {
  BellIcon,
  BitrixLogo,
  BitrixUrlInput,
  CommentIcon,
  FieldsIcon,
  FindIcon,
  Label,
  PhoneIcon,
  PlayIcon,
  RobotIcon,
  Segmented,
  StageIcon,
  StatusPill,
  ToggleRow,
  TodoIcon,
  TrashIcon,
  useInsert,
  VariableChips,
} from './kit'
import s from './integrations.module.css'

const outcomeOptions = [
  { value: 'appointment', label: 'Договорились о встрече' },
  { value: 'callback', label: 'Перезвонить' },
  { value: 'interested', label: 'Интерес есть' },
  { value: 'no_interest', label: 'Нет интереса' },
  { value: 'refusal', label: 'Отказ' },
  { value: 'hangup', label: 'Бросил трубку' },
  { value: 'wrong_number', label: 'Ошибочный номер' },
  { value: 'not_dm', label: 'Решает другой человек' },
  { value: 'unclear', label: 'Результат не определён' },
  { value: 'no_answer', label: 'Недозвон' },
  { value: 'busy', label: 'Занято' },
]

/* Настройки шагов — зеркало BitrixFlow на бэкенде. */
export interface BitrixFlow {
  targetMode: 'phone' | 'lead_id'
  leadIdField: string
  find: boolean
  ifMissing: 'nothing' | 'lead' | 'contact' | 'contact_deal'
  newTitle: string
  newCategoryId: number
  newStageId: string
  newResponsibleId: number
  stage: {
    enabled: boolean
    byOutcome: boolean
    dealStageId: string
    leadStatusId: string
    outcomeRoutes: { outcomes: string[]; leadStatusId: string }[]
  }
  fields: { enabled: boolean; items: { field: string; value: string }[] }
  comment: { enabled: boolean; text: string }
  callLog: { enabled: boolean; userId: number }
  todo: { enabled: boolean; when: 'next_action' | 'hours'; hours: number; title: string; responsibleId: number }
  notify: { enabled: boolean; userId: number; text: string }
  trigger: { enabled: boolean; code: string }
  successDeal: {
    enabled: boolean
    outcomes: string[]
    title: string
    categoryId: number
    stageId: string
    responsibleId: number
  }
}

/** Всё выключено: клиент сам решает, что делать в его Битриксе. */
export const emptyFlow = (): BitrixFlow => ({
  targetMode: 'phone',
  leadIdField: 'lead_id',
  find: false,
  ifMissing: 'nothing',
  newTitle: '{{data.contact.name}} — звонок бота',
  newCategoryId: 0,
  newStageId: '',
  newResponsibleId: 0,
  stage: { enabled: false, byOutcome: false, dealStageId: '', leadStatusId: '', outcomeRoutes: [] },
  fields: { enabled: false, items: [] },
  comment: { enabled: false, text: '' },
  callLog: { enabled: false, userId: 0 },
  todo: { enabled: false, when: 'next_action', hours: 24, title: '', responsibleId: 0 },
  notify: { enabled: false, userId: 0, text: '' },
  trigger: { enabled: false, code: '' },
  successDeal: {
    enabled: false,
    outcomes: ['appointment', 'callback', 'interested'],
    title: '{{data.contact.name}} — интерес после звонка',
    categoryId: 0,
    stageId: '',
    responsibleId: 0,
  },
})

export function parseFlow(raw: string): BitrixFlow {
  const base = emptyFlow()
  if (!raw) return base
  try {
    const f = JSON.parse(raw) as Partial<BitrixFlow>
    return {
      ...base,
      ...f,
      stage: { ...base.stage, ...f.stage, outcomeRoutes: f.stage?.outcomeRoutes ?? [] },
      fields: { ...base.fields, ...f.fields, items: f.fields?.items ?? [] },
      comment: { ...base.comment, ...f.comment },
      callLog: { ...base.callLog, ...f.callLog },
      todo: { ...base.todo, ...f.todo },
      notify: { ...base.notify, ...f.notify },
      trigger: { ...base.trigger, ...f.trigger },
      successDeal: { ...base.successDeal, ...f.successDeal, outcomes: f.successDeal?.outcomes ?? base.successDeal.outcomes },
    }
  } catch {
    return base
  }
}

/** Шаги, которым нужна найденная карточка клиента. */
const needsCard = (f: BitrixFlow) =>
  f.stage.enabled || f.fields.enabled || f.comment.enabled || f.callLog.enabled || f.todo.enabled || f.trigger.enabled || f.successDeal.enabled

export function countSteps(f: BitrixFlow): number {
  return [
    f.find || needsCard(f),
    f.ifMissing !== 'nothing',
    f.stage.enabled,
    f.fields.enabled,
    f.comment.enabled,
    f.callLog.enabled,
    f.todo.enabled,
    f.notify.enabled,
    f.trigger.enabled,
    f.successDeal.enabled,
  ].filter(Boolean).length
}

/* ------------------------------------------------------------ Builder */

export function BitrixBuilder({
  webhookId,
  flow,
  setFlow,
  secrets,
  setSecret,
  hints,
  catalog,
  event,
  variables,
}: {
  webhookId: number | null
  flow: BitrixFlow
  setFlow: (f: BitrixFlow) => void
  secrets: Secrets
  setSecret: (k: string, v: string) => void
  hints: Record<string, SecretHint>
  catalog: Catalog
  event: string
  variables: Variable[]
}) {
  const [meta, setMeta] = useState<BitrixMeta | null>(null)
  const [metaErr, setMetaErr] = useState('')
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<BitrixPreviewResult | null>(null)

  const hasUrl = !!(secrets.bitrix_url || hints.bitrix_url?.set)

  const loadMeta = async () => {
    setLoading(true)
    setMetaErr('')
    try {
      const m = await integrationsApi.bitrixMeta(webhookId, pick(secrets))
      setMeta(m)
      toast.success(`Портал подключён: ${m.portal}`)
    } catch (e) {
      setMeta(null)
      setMetaErr(errorText(e, 'Не удалось подключиться к порталу'))
    } finally {
      setLoading(false)
    }
  }

  // Портал уже сохранён — подтягиваем воронки, поля и сотрудников сразу.
  useEffect(() => {
    if (webhookId && hints.bitrix_url?.set && !meta) void loadMeta()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webhookId])

  const set = <K extends keyof BitrixFlow>(k: K, v: BitrixFlow[K]) => setFlow({ ...flow, [k]: v })
  const outcomeStatus = (outcome: string) =>
    flow.stage.outcomeRoutes.find((r) => r.outcomes.includes(outcome))?.leadStatusId ?? ''
  const setOutcomeStatus = (outcome: string, leadStatusId: string) => {
    const routes = flow.stage.outcomeRoutes
      .map((r) => ({ ...r, outcomes: r.outcomes.filter((x) => x !== outcome) }))
      .filter((r) => r.outcomes.length)
    if (leadStatusId) routes.push({ outcomes: [outcome], leadStatusId })
    set('stage', { ...flow.stage, outcomeRoutes: routes })
  }

  const previewMut = useAction((live: boolean) =>
    integrationsApi.bitrixPreview(webhookId, { secrets: pick(secrets), flow, event, live }),
  )
  const runPreview = (live: boolean) =>
    previewMut.mutate(live, { onSuccess: (r) => setPreview(r) })

  const users = meta?.users ?? []
  const userOpts = [{ value: '0', label: users.length ? 'Выберите сотрудника' : 'Сначала подключите портал' }].concat(
    users.map((u) => ({ value: u.id, label: u.title })),
  )
  const stageOpts = useMemo(() => {
    const st = meta?.dealStages ?? []
    return [{ value: '', label: 'Не менять стадию сделки' }].concat(
      st.map((x) => ({ value: x.id, label: x.group ? `${x.group} → ${x.title}` : x.title })),
    )
  }, [meta])
  const leadOpts = [{ value: '', label: 'Не менять статус лида' }].concat(
    (meta?.leadStatuses ?? []).map((x) => ({ value: x.id, label: x.title })),
  )
  const catOpts = [{ value: '0', label: 'Основная воронка' }].concat(
    (meta?.categories ?? []).filter((c) => c.id !== '0').map((c) => ({ value: c.id, label: c.title })),
  )
  const newStageOpts = [{ value: '', label: 'Первая стадия воронки' }].concat(
    (meta?.dealStages ?? [])
      .filter((x) => (flow.newCategoryId ? x.id.startsWith(`C${flow.newCategoryId}:`) : !x.id.includes(':')))
      .map((x) => ({ value: x.id, label: x.title })),
  )
  const successStageOpts = [{ value: '', label: 'Первая стадия воронки' }].concat(
    (meta?.dealStages ?? [])
      .filter((x) => (flow.successDeal.categoryId ? x.id.startsWith(`C${flow.successDeal.categoryId}:`) : !x.id.includes(':')))
      .map((x) => ({ value: x.id, label: x.title })),
  )
  const fieldOpts = useMemo(() => {
    const f = meta?.fields
    if (!f) return []
    const tag = (g: string, list: { id: string; title: string }[]) =>
      list.map((x) => ({ value: `${g}:${x.id}`, label: `${g === 'deal' ? 'Сделка' : g === 'lead' ? 'Лид' : 'Контакт'} · ${x.title}` }))
    return [...tag('deal', f.deal), ...tag('contact', f.contact), ...tag('lead', f.lead)]
  }, [meta])

  const applyTemplate = (raw: string) => {
    setFlow(parseFlow(raw))
    setPreview(null)
    toast.success('Шаблон применён — проверьте галочки и поменяйте, что нужно')
  }

  return (
    <div className={s.authBox}>
      {/* Подключение */}
      {meta ? (
        <div className={s.connected}>
          <BitrixLogo size={30} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={s.connectedTitle}>Портал подключён</div>
            <div className={s.muted}>
              {meta.portal} · воронок {meta.categories.length || 1}, сотрудников {meta.users.length}, полей сделки{' '}
              {meta.fields.deal.length}
            </div>
          </div>
          <Button size="sm" variant="secondary" onClick={() => void loadMeta()} loading={loading}>
            Обновить
          </Button>
        </div>
      ) : (
        <>
          <BitrixUrlInput hint={hints.bitrix_url} value={secrets.bitrix_url} onChange={(v) => setSecret('bitrix_url', v)} />
          <div className={s.condActions}>
            <Button size="sm" onClick={() => void loadMeta()} loading={loading} disabled={!hasUrl}>
              Подключить портал
            </Button>
            <span className={s.muted}>Покажем ваши воронки, стадии, поля и сотрудников — выбирать будете из списка.</span>
          </div>
          {metaErr && <div className={`${s.alert} ${s.alertBad}`}>{metaErr}</div>}
        </>
      )}
      {meta?.warnings?.length ? (
        <div className={s.alert}>
          Не хватает прав у вебхука: {meta.warnings.join('; ')}. Остальное работает.
        </div>
      ) : null}

      {/* Шаблоны */}
      <div className={s.formRow}>
        <Label hint="Расставят галочки за вас. Потом всё можно поменять.">Быстрый старт</Label>
        <div className={s.templates}>
          {catalog.bitrixTemplates.map((t) => (
            <button key={t.key} type="button" className={s.tpl} onClick={() => applyTemplate(t.flow)}>
              <span className={s.tplTitle}>{t.title}</span>
              <span className={s.tplText}>{t.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Шаги */}
      <div className={s.formRow}>
        <Label hint="Каждый шаг включается отдельно. Выключенное мы в Битриксе не трогаем.">
          Что делать в Битриксе — шаги по порядку
        </Label>
        <div className={s.toggleList}>
          <ToggleRow
            icon={<FindIcon />}
            title={flow.targetMode === 'lead_id' ? '1. Взять точный лид из заявки' : '1. Найти клиента по номеру'}
            text={flow.targetMode === 'lead_id'
              ? 'Пишем результат именно в лид, ID которого Битрикс передал вместе с заявкой.'
              : 'Ищем лид, контакт и его открытую сделку по телефону — чтобы не плодить дубли.'}
            on={flow.find || needsCard(flow) || flow.ifMissing !== 'nothing'}
            disabled={needsCard(flow) || flow.ifMissing !== 'nothing'}
            lockedHint="Включено автоматически: шагам ниже нужно знать, в какую карточку писать."
            onChange={(v) => set('find', v)}
          >
            <div className={s.formRow}>
              <Label hint="Если у одного номера несколько лидов, выбирайте точный ID — результат не попадёт в чужую карточку.">
                Как выбрать карточку
              </Label>
              <Segmented
                value={flow.targetMode}
                onChange={(v) => setFlow({
                  ...flow,
                  targetMode: v,
                  find: true,
                  ifMissing: v === 'lead_id' ? 'nothing' : flow.ifMissing,
                  leadIdField: flow.leadIdField || 'lead_id',
                })}
                options={[
                  { value: 'phone', label: 'Найти по телефону' },
                  { value: 'lead_id', label: 'Точный ID лида' },
                ]}
              />
            </div>
            {flow.targetMode === 'lead_id' ? (
              <div className={s.formRow}>
                <Label hint="Во входящей ссылке сопоставьте параметр lead_id с полем «ID лида в Битрикс24».">
                  Поле с ID лида
                </Label>
                <Input
                  value={flow.leadIdField}
                  placeholder="lead_id"
                  onChange={(e) => set('leadIdField', e.target.value)}
                />
                <div className={s.muted}>
                  Пример: <code>?lead_id=123&amp;phone=79001234567</code>. Если ID не пришёл, в Битрикс ничего не отправим и покажем ошибку в журнале.
                </div>
              </div>
            ) : (
              <>
            <div className={s.formRow}>
              <Label>Если клиента в Битриксе нет</Label>
              <Segmented
                value={flow.ifMissing}
                onChange={(v) => set('ifMissing', v)}
                options={[
                  { value: 'nothing', label: 'Ничего не делать' },
                  { value: 'lead', label: 'Создать лид' },
                  { value: 'contact', label: 'Создать контакт' },
                  { value: 'contact_deal', label: 'Контакт + сделка' },
                ]}
              />
            </div>
            {flow.ifMissing !== 'nothing' && (
              <>
                <TemplateField
                  label="Название"
                  value={flow.newTitle}
                  onChange={(v) => set('newTitle', v)}
                  variables={variables}
                  single
                />
                {flow.ifMissing === 'contact_deal' && (
                  <div className={s.grid2}>
                    <div className={s.formRow}>
                      <Label>Воронка</Label>
                      <Select
                        value={String(flow.newCategoryId)}
                        onChange={(e) => setFlow({ ...flow, newCategoryId: Number(e.target.value), newStageId: '' })}
                        options={catOpts}
                      />
                    </div>
                    <div className={s.formRow}>
                      <Label>Стадия новой сделки</Label>
                      <Select value={flow.newStageId} onChange={(e) => set('newStageId', e.target.value)} options={newStageOpts} />
                    </div>
                  </div>
                )}
                <div className={s.formRow}>
                  <Label>Ответственный</Label>
                  <Select
                    value={String(flow.newResponsibleId)}
                    onChange={(e) => set('newResponsibleId', Number(e.target.value))}
                    options={[{ value: '0', label: 'По умолчанию портала' }].concat(users.map((u) => ({ value: u.id, label: u.title })))}
                  />
                </div>
              </>
            )}
              </>
            )}
          </ToggleRow>

          <ToggleRow
            icon={<StageIcon />}
            title="2. Сменить стадию"
            text="Двигаем найденную сделку или лид в выбранную стадию."
            on={flow.stage.enabled}
            onChange={(v) => set('stage', { ...flow.stage, enabled: v })}
          >
            <div className={s.formRow}>
              <Label>Как выбирать статус</Label>
              <Segmented
                value={flow.stage.byOutcome ? 'outcome' : 'single'}
                onChange={(v) => set('stage', { ...flow.stage, byOutcome: v === 'outcome', outcomeRoutes: v === 'outcome' ? flow.stage.outcomeRoutes : [] })}
                options={[
                  { value: 'single', label: 'Один статус' },
                  { value: 'outcome', label: 'По результату звонка' },
                ]}
              />
            </div>
            {flow.stage.byOutcome ? (
              <div className={s.formRow}>
                {outcomeOptions.map((outcome) => (
                  <div key={outcome.value} className={s.mapRow}>
                    <span>{outcome.label}</span>
                    <span className={s.mapArrow}>→</span>
                    <Select
                      value={outcomeStatus(outcome.value)}
                      onChange={(e) => setOutcomeStatus(outcome.value, e.target.value)}
                      options={leadOpts}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className={s.grid2}>
                <div className={s.formRow}>
                  <Label>Сделка → стадия</Label>
                  <Select value={flow.stage.dealStageId}
                    onChange={(e) => set('stage', { ...flow.stage, dealStageId: e.target.value })} options={stageOpts} />
                </div>
                <div className={s.formRow}>
                  <Label>Лид → статус</Label>
                  <Select value={flow.stage.leadStatusId}
                    onChange={(e) => set('stage', { ...flow.stage, leadStatusId: e.target.value })} options={leadOpts} />
                </div>
              </div>
            )}
          </ToggleRow>

          <ToggleRow
            icon={<FieldsIcon />}
            title="3. Заполнить поля"
            text="Отмечаете только нужные поля портала — остальные не трогаем."
            on={flow.fields.enabled}
            onChange={(v) =>
              set('fields', {
                enabled: v,
                items: flow.fields.items.length ? flow.fields.items : [{ field: '', value: '' }],
              })
            }
          >
            {flow.fields.items.map((it, i) => (
              <div key={i} className={s.mapRow}>
                <Select
                  value={it.field}
                  onChange={(e) =>
                    set('fields', { ...flow.fields, items: flow.fields.items.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)) })
                  }
                  options={[{ value: '', label: fieldOpts.length ? 'Поле в Битриксе' : 'Подключите портал' }, ...withValue(fieldOpts, it.field)]}
                />
                <span className={s.mapArrow}>←</span>
                <Select
                  value={it.value}
                  onChange={(e) =>
                    set('fields', { ...flow.fields, items: flow.fields.items.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })
                  }
                  options={[{ value: '', label: 'Наше значение' }, ...withValue(
                    variables.map((v) => ({ value: `{{${v.path}}}`, label: prettyLabel(v.path) })),
                    it.value,
                  )]}
                />
                <button type="button" className={s.iconBtn} aria-label="Убрать поле"
                  onClick={() => set('fields', { ...flow.fields, items: flow.fields.items.filter((_, j) => j !== i) })}>
                  <TrashIcon />
                </button>
              </div>
            ))}
            <button type="button" className={s.addLink}
              onClick={() => set('fields', { ...flow.fields, items: [...flow.fields.items, { field: '', value: '' }] })}>
              + Ещё поле
            </button>
          </ToggleRow>

          <ToggleRow
            icon={<CommentIcon />}
            title="4. Комментарий в ленту"
            text="Итог разговора появится в ленте карточки."
            on={flow.comment.enabled}
            onChange={(v) =>
              set('comment', {
                enabled: v,
                text: flow.comment.text || 'Звонок бота — {{data.report.outcomeTitle}}\n{{data.report.summary}}\nЗапись: {{data.call.recordingUrl}}',
              })
            }
          >
            <TemplateField value={flow.comment.text} onChange={(v) => set('comment', { ...flow.comment, text: v })} variables={variables} />
          </ToggleRow>

          <ToggleRow
            icon={<PhoneIcon />}
            title="5. Звонок в историю"
            text="Звонок с длительностью и записью — как будто звонил сотрудник."
            on={flow.callLog.enabled}
            onChange={(v) => set('callLog', { ...flow.callLog, enabled: v })}
          >
            <div className={s.formRow}>
              <Label>От чьего имени записать</Label>
              <Select value={String(flow.callLog.userId)}
                onChange={(e) => set('callLog', { ...flow.callLog, userId: Number(e.target.value) })} options={userOpts} />
            </div>
          </ToggleRow>

          <ToggleRow
            icon={<StageIcon />}
            title="6. Создать сделку при интересе"
            text="После записи результата в лид создаём связанную сделку только для встречи, обратного звонка или интереса."
            on={flow.successDeal.enabled}
            onChange={(v) => set('successDeal', { ...flow.successDeal, enabled: v })}
          >
            <TemplateField
              label="Название сделки"
              value={flow.successDeal.title}
              onChange={(v) => set('successDeal', { ...flow.successDeal, title: v })}
              variables={variables}
              single
            />
            <div className={s.grid2}>
              <div className={s.formRow}>
                <Label>Воронка</Label>
                <Select
                  value={String(flow.successDeal.categoryId)}
                  onChange={(e) => set('successDeal', { ...flow.successDeal, categoryId: Number(e.target.value), stageId: '' })}
                  options={catOpts}
                />
              </div>
              <div className={s.formRow}>
                <Label>Стадия новой сделки</Label>
                <Select
                  value={flow.successDeal.stageId}
                  onChange={(e) => set('successDeal', { ...flow.successDeal, stageId: e.target.value })}
                  options={successStageOpts}
                />
              </div>
            </div>
            <div className={s.formRow}>
              <Label>Ответственный</Label>
              <Select
                value={String(flow.successDeal.responsibleId)}
                onChange={(e) => set('successDeal', { ...flow.successDeal, responsibleId: Number(e.target.value) })}
                options={[{ value: '0', label: 'По умолчанию портала' }].concat(users.map((u) => ({ value: u.id, label: u.title })))}
              />
            </div>
          </ToggleRow>

          <ToggleRow
            icon={<TodoIcon />}
            title="7. Дело менеджеру"
            text="«Перезвонить» на дату, о которой договорился бот."
            on={flow.todo.enabled}
            onChange={(v) =>
              set('todo', { ...flow.todo, enabled: v, title: flow.todo.title || 'Перезвонить: {{data.contact.name}} {{data.contact.phone}}' })
            }
          >
            <TemplateField label="Что сделать" value={flow.todo.title} single
              onChange={(v) => set('todo', { ...flow.todo, title: v })} variables={variables} />
            <div className={s.grid2}>
              <div className={s.formRow}>
                <Label>Когда</Label>
                <Segmented
                  size="sm"
                  value={flow.todo.when}
                  onChange={(v) => set('todo', { ...flow.todo, when: v })}
                  options={[
                    { value: 'next_action', label: 'Как договорился бот' },
                    { value: 'hours', label: 'Через N часов' },
                  ]}
                />
              </div>
              <div className={s.formRow}>
                <Label hint={flow.todo.when === 'next_action' ? 'если даты в разговоре не было' : undefined}>Через, часов</Label>
                <Input type="number" min={1} value={flow.todo.hours}
                  onChange={(e) => set('todo', { ...flow.todo, hours: Number(e.target.value) || 0 })} />
              </div>
            </div>
            <div className={s.formRow}>
              <Label>Ответственный</Label>
              <Select value={String(flow.todo.responsibleId)}
                onChange={(e) => set('todo', { ...flow.todo, responsibleId: Number(e.target.value) })}
                options={[{ value: '0', label: 'Ответственный по карточке' }].concat(users.map((u) => ({ value: u.id, label: u.title })))} />
            </div>
          </ToggleRow>

          <ToggleRow
            icon={<BellIcon />}
            title="8. Уведомить сотрудника"
            text="Сообщение в колокольчик Битрикса."
            on={flow.notify.enabled}
            onChange={(v) =>
              set('notify', {
                ...flow.notify,
                enabled: v,
                text: flow.notify.text || '{{data.contact.name}} {{data.contact.phone}}: {{data.report.summary}}',
              })
            }
          >
            <div className={s.formRow}>
              <Label>Кому</Label>
              <Select value={String(flow.notify.userId)}
                onChange={(e) => set('notify', { ...flow.notify, userId: Number(e.target.value) })} options={userOpts} />
            </div>
            <TemplateField label="Текст" value={flow.notify.text}
              onChange={(v) => set('notify', { ...flow.notify, text: v })} variables={variables} />
          </ToggleRow>

          <ToggleRow
            icon={<RobotIcon />}
            title="9. Запустить вашу автоматизацию"
            text="Нажимаем ваш триггер в Битриксе — дальше работают ваши роботы."
            on={flow.trigger.enabled}
            onChange={(v) => set('trigger', { ...flow.trigger, enabled: v })}
          >
            <div className={s.formRow}>
              <Label hint="CRM → Роботы → Триггеры → «Входящий вебхук», скопируйте код">Код триггера</Label>
              <Input value={flow.trigger.code} placeholder="bot_called"
                onChange={(e) => set('trigger', { ...flow.trigger, code: e.target.value })} />
            </div>
          </ToggleRow>
        </div>
      </div>

      {/* Проверка */}
      <div className={s.resultBox}>
        <div className={s.resultHead}>
          <strong style={{ flex: 1 }}>Что уйдёт в Битрикс</strong>
          <Button size="sm" variant="secondary" onClick={() => runPreview(false)} loading={previewMut.isPending && !previewMut.variables}
            disabled={!hasUrl}>
            <span className={s.btnRow}><PlayIcon /> Показать план</span>
          </Button>
          <Button size="sm" variant="secondary" disabled={!hasUrl}
            loading={previewMut.isPending && !!previewMut.variables}
            onClick={() => {
              if (window.confirm('Выполнить шаги по-настоящему в вашем Битриксе на последнем звонке?')) runPreview(true)
            }}>
            Выполнить на последнем звонке
          </Button>
        </div>
        <div className={s.resultBody}>
          {!preview && (
            <span className={s.muted}>
              «Показать план» ничего не меняет: находим клиента по номеру и показываем, что сделаем. «Выполнить» — по-настоящему.
            </span>
          )}
          {preview && <BitrixRunView result={preview} />}
        </div>
      </div>
    </div>
  )
}

export function BitrixRunView({ result }: { result: BitrixPreviewResult }) {
  return (
    <div className={s.flowRun}>
      {result.sample && (
        <div className={`${s.alert} ${s.alertInfo}`}>Звонков ещё не было — показываем на примере.</div>
      )}
      {result.error && <div className={`${s.alert} ${s.alertBad}`}>{result.error}</div>}
      {result.run?.steps?.map((st, i) => (
        <div key={i} className={s.flowStep}>
          <span className={s.flowStepIdx}>{i + 1}</span>
          <div className={s.flowStepMain}>
            <div className={s.flowStepTitle}>{st.title}</div>
            <div className={s.flowStepDetail}>{st.detail}</div>
          </div>
          <StatusPill status={st.status} />
        </div>
      ))}
      {result.run && !result.run.steps?.length && <span className={s.muted}>Ни один шаг не включён.</span>}
      <span className={s.muted}>
        {result.live ? 'Выполнено' : 'План построен'} за {result.durationMs} мс
      </span>
    </div>
  )
}

function TemplateField({
  label,
  value,
  onChange,
  variables,
  single,
}: {
  label?: string
  value: string
  onChange: (v: string) => void
  variables: Variable[]
  single?: boolean
}) {
  const { ref, insert } = useInsert<HTMLTextAreaElement>(value, onChange)
  return (
    <div className={s.formRow}>
      {label && <Label>{label}</Label>}
      <textarea
        ref={ref}
        className={`${s.textarea} ${s.textareaPlain}`}
        rows={single ? 1 : 3}
        style={single ? { minHeight: 40 } : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <VariableChips variables={variables} onInsert={insert} limit={10} />
    </div>
  )
}

function withValue(list: { value: string; label: string }[], cur: string) {
  if (!cur || list.some((x) => x.value === cur)) return list
  return [{ value: cur, label: cur }, ...list]
}

function prettyLabel(path: string) {
  const map: Record<string, string> = {
    'data.contact.name': 'Имя клиента',
    'data.contact.phone': 'Телефон',
    'data.contact.labelTitle': 'Оценка клиента',
    'data.contact.summary': 'Резюме по клиенту',
    'data.report.summary': 'Итог разговора',
    'data.report.outcomeTitle': 'Результат звонка',
    'data.report.nextActionAt': 'Когда перезвонить',
    'data.call.url': 'Ссылка на звонок',
    'data.call.recordingUrl': 'Запись',
    'data.call.durationSec': 'Длительность, сек',
  }
  return map[path] ?? path.replace(/^data\./, '')
}

/** Только реально введённые секреты. */
function pick(secrets: Secrets): Secrets {
  const out: Secrets = {}
  for (const [k, v] of Object.entries(secrets)) if (v) out[k] = v
  return out
}
